/**
 * @file authService.ts
 * @brief Auth API: login, logout, me, refresh. Unwraps backend { success, data } to payload.
 */

import axios from 'axios';
import Cookies from 'js-cookie';
import apiClient from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import { User, UserRole } from '@/types';

const REFRESH_TOKEN_COOKIE = 'refreshToken';
const AUTH_TOKEN_COOKIE = 'authToken';

export interface LoginPayload {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName: string;
    role: string;
    departmentId: string;
    teamId: string | null;
  };
}

const roleMap: Record<string, UserRole> = {
  super_admin: UserRole.SUPER_ADMIN,
  admin: UserRole.ADMIN,
  department_head: UserRole.DEPARTMENT_HEAD,
  project_manager: UserRole.PROJECT_MANAGER,
  team_lead: UserRole.TEAM_LEAD,
  engineer: UserRole.ENGINEER,
  individual_contributor: UserRole.INDIVIDUAL_CONTRIBUTOR,
  manager: UserRole.MANAGER,
};

export function mapBackendUserToFrontend(backendUser: LoginPayload['user']): User {
  const role = roleMap[backendUser.role?.toLowerCase()] ?? UserRole.INDIVIDUAL_CONTRIBUTOR;
  return {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.firstName ?? 'User',
    lastName: backendUser.lastName ?? '',
    displayName: backendUser.displayName ?? `${backendUser.firstName ?? 'User'} ${backendUser.lastName ?? ''}`.trim(),
    role,
    departmentId: backendUser.departmentId ?? '',
    teamId: backendUser.teamId ?? undefined,
    isActive: true,
    workHoursPerDay: 8,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: '',
  };
}

export async function login(email: string, password: string): Promise<LoginPayload> {
  try {
    const response = await apiClient.post<{ success: boolean; data?: LoginPayload; message?: string }>(
      API_ENDPOINTS.AUTH.LOGIN,
      { email, password }
    );
    const body = response.data;
    if (!body.success || !body.data) {
      throw new Error((body as { message?: string }).message ?? 'Login failed');
    }
    return body.data;
  } catch (error: any) {
    // Extract error message from response data
    const message = error.response?.data?.message || error.message || 'Login failed';
    throw new Error(message);
  }
}

/**
 * Refresh access token using stored refresh token. Does not use apiClient to avoid 401 interceptor loop.
 * @return New access token.
 */
export async function refreshAccessToken(): Promise<string> {
  const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE);
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  const baseURL = process.env.REACT_APP_BASE_URL ?? '';
  const response = await axios.post<{ success: boolean; data?: { accessToken: string }; message?: string }>(
    `${baseURL}${API_ENDPOINTS.AUTH.REFRESH}`,
    { refreshToken },
    { timeout: 10000 }
  );
  const body = response.data;
  if (!body.success || !body.data?.accessToken) {
    throw new Error((body as { message?: string }).message ?? 'Refresh failed');
  }
  return body.data.accessToken;
}

export async function logout(): Promise<void> {
  const token = Cookies.get(AUTH_TOKEN_COOKIE);
  if (token) {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      // Ignore network errors on logout
    }
  }
  Cookies.remove(AUTH_TOKEN_COOKIE);
  Cookies.remove(REFRESH_TOKEN_COOKIE);
  Cookies.remove('userRole');
  Cookies.remove('email');
}

export async function getMe(): Promise<User> {
  const response = await apiClient.get<{ success: boolean; data?: LoginPayload['user']; message?: string }>(
    API_ENDPOINTS.AUTH.ME
  );
  const body = response.data;
  if (!body.success || !body.data) {
    throw new Error((body as { message?: string }).message ?? 'Not authenticated');
  }
  return mapBackendUserToFrontend(body.data);
}
